/**
 * Ad-platform click ID capture.
 *
 * Reads click IDs from URL params on landing and persists them in
 * first-party cookies on the parent domain (.newsquawk.com) so both
 * the marketing site and the portal can read them. sGTM consumes them
 * server-side to send Meta CAPI / Google Ads / LinkedIn CAPI / Bing
 * conversions with platform-native attribution.
 *
 * Cookies are scoped to .newsquawk.com on production hosts so they
 * cross subdomains; on localhost the Domain attribute is omitted so
 * the cookie scopes to the current host.
 */

const CLICK_ID_PARAMS = [
  "gclid",
  "fbclid",
  "li_fat_id",
  "msclkid",
  "ttclid",
] as const;

type ClickIdParam = (typeof CLICK_ID_PARAMS)[number];

export type ClickIds = Partial<Record<ClickIdParam, string>>;

const COOKIE_PREFIX = "nq_clk_";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

/**
 * When the stored click IDs were captured, as epoch milliseconds.
 *
 * Meta's Conversions API doesn't take a bare `fbclid` — it wants an `fbc`
 * of the form `fb.1.<click_time_ms>.<fbclid>`, and a wrong or missing
 * timestamp costs the match. Click IDs are last-touch, so this is
 * rewritten alongside them.
 *
 * A separate cookie rather than a field on each `nq_clk_*` value, because
 * those are already in the wild with 90-day lifetimes: reshaping them
 * would leave a quarter of readers parsing a format that doesn't match.
 * All click IDs on one landing share a capture moment, so one covers all.
 */
const CLICK_ID_TS_COOKIE = "nq_clk_ts";

function isLocalhost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  const hostname = window.location.hostname;
  const local = isLocalhost(hostname);
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (!local) {
    parts.push("Domain=.newsquawk.com");
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

/**
 * Reads click IDs from `window.location.search` and writes any present
 * values to first-party cookies. Existing cookies are preserved unless
 * the URL carries a fresher value for the same param.
 *
 * Call once at app mount alongside captureAttribution().
 */
export function captureClickIds(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    let captured = false;
    for (const param of CLICK_ID_PARAMS) {
      const value = params.get(param);
      if (value) {
        writeCookie(`${COOKIE_PREFIX}${param}`, value);
        captured = true;
      }
    }
    // Stamped only when something was captured, so it always describes the
    // IDs currently stored rather than implying a click that never happened.
    if (captured) {
      writeCookie(CLICK_ID_TS_COOKIE, String(Date.now()));
    }
  } catch {
    // Cookie/URL parsing failure — silently ignore, tracking must never break the app
  }
}

/**
 * When the stored click IDs were captured, as epoch milliseconds, or null
 * when unknown — the normal case for a visitor whose click IDs predate
 * this cookie.
 */
export function getClickIdTimestamp(): string | null {
  try {
    const raw = readCookie(CLICK_ID_TS_COOKIE);
    return raw && /^\d+$/.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Reads currently-stored click IDs from cookies. Returns an object
 * containing only the params that have non-empty values.
 */
export function getClickIds(): ClickIds {
  const result: ClickIds = {};
  try {
    for (const param of CLICK_ID_PARAMS) {
      const value = readCookie(`${COOKIE_PREFIX}${param}`);
      if (value) result[param] = value;
    }
  } catch {
    // Cookie read failure — return whatever we got so far
  }
  return result;
}
